import { Checkbox, Input } from '@seiue/ui'
import { GrowpIcon } from 'packages/components/GrowpIcon'
import React, { useContext } from 'react'
import styled from 'styled-components'
import { QuestionnaireContext } from './context'
import { FieldTypeEnum } from 'packages/sdks-next/growp'
import {
  changeMetaData,
  changeQuestionnaireData,
  copyItemFormat,
  typeToLabel,
} from './utils'
import { ReqWithMetaData } from './types'
import { cloneDeep } from '@seiue/util'
import { getTheme } from '@/utils/theme'
const theme = getTheme()

/**
 * 问卷 - 题目基础组件
 *
 * @param param - 参数
 * @param param.isDrag - 是否可拖动
 * @param param.currentIndex - 当前题目索引|包括描述和分页
 * @param param.content - React.ReactNode
 * @param param.questionnaire - 当前题目结构数据
 * @param param.serialNumber - 当前题目序号|不包括描述和分页
 * @returns
 */
export const BaseItem: React.FC<{
  isDrag: boolean
  currentIndex: number
  content: React.ReactNode
  questionnaire: ReqWithMetaData
  serialNumber?: number
}> = ({ isDrag, currentIndex, content, questionnaire, serialNumber }) => {
  const {
    questionnaires,
    setQuestionnaires,
    activeItemIndex,
    setActiveItemIndex,
    structIsChange,
  } = useContext(QuestionnaireContext)

  const { metadata } = questionnaire

  const deleteItem = (type: FieldTypeEnum) => {
    const preQuestionnaires = questionnaires || []
    preQuestionnaires.splice(currentIndex, 1)
    setQuestionnaires([...preQuestionnaires])
    setActiveItemIndex(undefined)
    if (![FieldTypeEnum.Describe, FieldTypeEnum.PageBreak].includes(type)) {
      structIsChange && structIsChange()
    }
  }

  const copyItem = (type: FieldTypeEnum) => {
    const preQuestionnaires = questionnaires || []
    const copyItem = cloneDeep(preQuestionnaires[currentIndex])
    preQuestionnaires.splice(
      currentIndex + 1,
      0,
      copyItemFormat(copyItem) as ReqWithMetaData
    )
    setQuestionnaires([...preQuestionnaires])
    if (![FieldTypeEnum.Describe, FieldTypeEnum.PageBreak].includes(type)) {
      structIsChange && structIsChange()
    }
  }

  const onChangeData = (params: object) => {
    const newQuestionnaires = changeQuestionnaireData(
      questionnaires,
      currentIndex,
      { ...questionnaire, ...params }
    )
    setQuestionnaires([...newQuestionnaires])
  }

  return (
    <Wrapper
      className={
        activeItemIndex === currentIndex ? 'active-item-wrapper' : undefined
      }
      style={{
        opacity: isDrag ? 0.8 : 1,
      }}
      onClick={(e) => {
        e.stopPropagation()
        setActiveItemIndex(currentIndex)
      }}
    >
      <Top className="dragdot">
        <GrowpIcon
          name="Drag"
          size={20}
          color={theme.text._3}
          className="dragdot-icon"
        />
      </Top>
      <Content>
        {![FieldTypeEnum.Describe, FieldTypeEnum.PageBreak].includes(
          questionnaire.type
        ) && (
          <>
            <TitleWrapper>
              <div>{questionnaire.require ? '*' : undefined}</div>
              <Input
                id={`${currentIndex}`}
                disabled={!!questionnaire.isUserField}
                autoFocus={false}
                underlined
                prefix={`${serialNumber}.`}
                placeholder="请输入标题"
                size="large"
                value={questionnaire.label}
                onChange={(e) => onChangeData({ label: e.target.value })}
                style={{
                  color: theme.text._1,
                }}
                onBlur={(e) => {
                  const v = e.target.value
                  if (!v) {
                    onChangeData({ label: typeToLabel.get(questionnaire.type) })
                  }
                }}
                maxLength={200}
              />
            </TitleWrapper>
            {!!metadata?.showDescription ||
            (questionnaire.isUserField && questionnaire.description) ? (
              <DescriptionWrapper>
                <Input
                  disabled={!!questionnaire.isUserField}
                  autoFocus={false}
                  underlined
                  style={{
                    padding: '0 0',
                    marginTop: '8px',
                    color: theme.text._2,
                  }}
                  placeholder="请输入说明文案"
                  value={questionnaire.description}
                  onChange={(e) =>
                    onChangeData({ description: e.target.value })
                  }
                  maxLength={1000}
                />
              </DescriptionWrapper>
            ) : null}
          </>
        )}
        <InnerContent
          style={{
            padding: [FieldTypeEnum.Single, FieldTypeEnum.Multi].includes(
              questionnaire.type
            )
              ? '0 40px 0 0'
              : '0 40px 0 40px',
          }}
        >
          {content}
        </InnerContent>
      </Content>
      <BottomWrapper>
        <Bottom className="bottom-area">
          {![FieldTypeEnum.Describe, FieldTypeEnum.PageBreak].includes(
            questionnaire.type
          ) && (
            <CheckboxWrapper>
              <Checkbox
                disabled={!!questionnaire.isUserField}
                onChange={(e) => {
                  const newQuestionnaire = changeMetaData(
                    questionnaires,
                    currentIndex,
                    {
                      showDescription: e.target.checked,
                    }
                  )
                  setQuestionnaires([...newQuestionnaire])
                }}
                checked={!!metadata?.showDescription}
              />
              <span>题目说明</span>
            </CheckboxWrapper>
          )}
          {!questionnaire.isUserField ? (
            <div onClick={() => copyItem(questionnaire.type)}>
              <GrowpIcon
                name="Copy"
                text="复制"
                size={14}
                color={theme.text._2}
                textMarginLeft={5}
              />
            </div>
          ) : null}
          <div
            onClick={(e) => {
              e.stopPropagation()
              deleteItem(questionnaire.type)
            }}
          >
            <GrowpIcon
              name="Delete"
              text="删除"
              size={14}
              color={theme.text._2}
              textMarginLeft={5}
            />
          </div>
        </Bottom>
      </BottomWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 760px;
  min-height: 52px;
  background-color: #ffffff;
  margin-bottom: 20px;

  &:hover {
    box-shadow: 0px 0px 1px 0px rgba(21, 29, 41, 0.1),
      0px 0px 8px 0px rgba(21, 29, 41, 0.02),
      0px 6px 8px 0px rgba(21, 29, 41, 0.08);
  }
  .dragdot-icon,
  .bottom-area {
    display: none;
  }
  &:hover,
  &:active,
  &:focus {
    .dragdot-icon {
      display: block;
    }
  }
  &&& {
    .ant-input-affix-wrapper {
      border-bottom: 1px solid transparent;
    }
    .ant-input-affix-wrapper-focused,
    .ant-input-affix-wrapper:hover {
      border-bottom: 1px solid ${(props) => props.theme.brand._1};
    }
    .ant-input-affix-wrapper-disabled {
      background-color: transparent;
      & .ant-input {
        color: ${(props) => props.theme.text._1};
      }
      &:hover {
        border-bottom-color: transparent;
      }
    }
  }
`

const Top = styled.div`
  width: 100%;
  height: 20px;
  display: flex;
  justify-content: center;
`

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 40px 0 24px;
  & > div {
    width: 17px;
    text-align: center;
    font-size: 16px;
    padding-top: 7px;
    color: #f54545;
  }
  &&& {
    .ant-input-prefix,
    .ant-input {
      font-weight: 500;
      color: ${(props) => props.theme.text._1};
    }
  }
`

const Content = styled.div`
  width: 100%;
  .ant-input-affix-wrapper-borderless {
    padding: 0 0;
  }
`

const BottomWrapper = styled.div`
  width: 100%;
  height: 32px;
`

const Bottom = styled.div`
  width: 100%;
  height: 32px;
  background-color: ${(props) => props.theme.background._4};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 0 40px;
`

const CheckboxWrapper = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => props.theme.text._2};
  & > span {
    margin-left: 8px;
  }
`

const DescriptionWrapper = styled.div`
  padding: 0 40px;
  & .ant-input,
  .ant-input-disabled {
    border-bottom: 1px solid transparent;
  }
  & .ant-input-disabled {
    background-color: transparent;
  }
  & .ant-input:hover,
  .ant-input:focus {
    border-bottom: 1px solid ${(props) => props.theme.brand._1} !important;
  }
  & .ant-input-disabled:hover,
  .ant-input-disabled:focus {
    border-bottom: 1px solid transparent !important;
  }
`

const InnerContent = styled.div`
  /* padding: 0 40px; */
`
